import ActionButton from "Components/atoms/buttons/action-button"
import { HelpIcon } from "Components/atoms/icons"
import withRouter from "Components/Common/withRouter"
import ClientEngagementForm from "Components/organisms/forms/client-engagement-form"
import ClientEngagementFields from "Components/organisms/forms/client-engagement-form/types"
import FormPageTemplate from "Components/templates/form-page-template"
import { ClientEngagementValidation } from "data/client-engagement"
import { FormikHelpers, useFormik } from "formik"
import useManual from "hooks/useManual"
import Client from "models/client"
import Opportunity from "models/opportunity"
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { createSelector } from "reselect"
import { addClientEngagement, ClientEngagementSliceState, setClientEngagementSearchFields } from "slices/business-development/client-engagement/reducer"
import { getClientEngagementSearchFields, getEmployeeFormFieldValues, submitCreateClientEngagementForm, submitConsultantForm, submitPotentialConsultantForm } from "slices/thunk"
import Modal from "Components/molecule/modal";
import AreYouSureButtons from "Components/molecule/are-you-sure-buttons"


const selectClientEngagement = createSelector(
	(state: any) => state.ClientEngagement,
	({
		opportunities,
		clients
	}: ClientEngagementSliceState) => ({
		opportunities,
		clients
	})
)

const CreateClientEngagementPage = (props: any) => {
	document.title = "Create Client Engagement | IbexIQ";
	const { handleOpenManual } = useManual("business-development/client-engagement");
	const dispatch: any = useDispatch();
	const [showAreYouSureModal, setShowAreYouSureModal] = useState<boolean>(false)
	const {
		opportunities,
		clients
	} = useSelector(selectClientEngagement)

	const initializePage = async () => {

		if (
			opportunities.length === 0 ||
			clients.length === 0
		) {
			const res = await getClientEngagementSearchFields()
			if (!res) {
				return;
			}
			dispatch(setClientEngagementSearchFields(res))
		}
	}

	useEffect(() => {
		initializePage()
	}, [])


	const handleSubmitClientEngagementForm = (_vals: ClientEngagementFields, helpers: FormikHelpers<ClientEngagementFields>) => {
		helpers.setSubmitting(false)
		setShowAreYouSureModal(true)
	}

	const initialValues: ClientEngagementFields = useMemo(() => ({
		opportunityId: '',
		clientId: '',
		notes: '',
		dateOfNextCheckin: ''
	}), [])

	const {
		values,
		errors,
		touched,
		handleSubmit,
		handleBlur,
		handleChange,
		isValid,
		setSubmitting,
		isSubmitting,
		handleReset,
		setFieldValue
	} = useFormik({
		onSubmit: handleSubmitClientEngagementForm,
		enableReinitialize: true,
		initialValues: initialValues,
		validationSchema: ClientEngagementValidation
	})

	const handleBack = () => {
		props.router.navigate(-1)
		setShowAreYouSureModal(false)
	}

	const handleModalClose = () => setShowAreYouSureModal(false);

	const hanldeConfirmSubmitClientEngagementForm = useCallback(async (values: ClientEngagementFields, setSubmitting: (val: boolean) => any, reset: (e: any) => void) => {
		setSubmitting(true);
		const res = await submitCreateClientEngagementForm(values, () => reset({}))

		if (!res) {
			handleModalClose()
		} else {
			handleBack()
			dispatch(addClientEngagement(res))
		}
		setSubmitting(false)
	}, [handleBack, handleModalClose])

	const handleClearOpportunity = () => {
		setFieldValue('opportunityId', '')
	}

	return <FormPageTemplate
		title={"Business Development"}
		breadcrumbItem={"Create Client Engagement"}
		actions={<ActionButton onClick={handleOpenManual}>Help <HelpIcon /></ActionButton>}
		form={<ClientEngagementForm
			clients={[...clients.map((bA: Client) => ({ value: bA.id, label: bA.name }))]}
			opportunities={[...opportunities.map((o: Opportunity) => ({ value: o.id, label: o.description }))]}
			values={values}
			errors={errors}
			touched={touched}
			handleChange={handleChange}
			handleBlur={handleBlur}
			handleSubmit={handleSubmit}
			handleCancel={handleBack}
			isValid={isValid}
			isSubmitting={isSubmitting}
		/>}
		isLoading={false}
		modal={<Modal
			isOpen={showAreYouSureModal}
			onClose={handleModalClose}
			title={"Confirm"}
			subtitle={"Are you sure want to create client engagement?"}
		>
			<AreYouSureButtons
				disable={isSubmitting}
				acceptButtonStyle={{ width: '150px' }}
				declineButtonStyle={{ width: '150px' }}
				handleAccept={() => hanldeConfirmSubmitClientEngagementForm(values, setSubmitting, handleReset)}
				handleDecline={handleModalClose}
				acceptText={isSubmitting ? "Submitting..." : "Submit"}
				declineText={"Cancel"}
			/>
		</Modal>}
	/>
}

export default withRouter(CreateClientEngagementPage);